import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { FaPlus, FaChevronDown, FaCalendarAlt, FaRegClock, FaTrashAlt } from 'react-icons/fa';
import Timekeeper from 'react-timekeeper';
import Button from 'common/components/button/Button';
import CheckBox from 'common/components/checkBox/CheckBox';
import InputGroup from 'common/components/inputGroup/InputGroup';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import { TimeKeeperDoneButton } from 'common/components/timeKeeperDoneButton/TimeKeeperDoneButton';
import { DATEPICKER_FORMAT_DEFAULT } from 'Config';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import PrivacyService from 'api/privacy/PrivacyService';
import { ScheduleSlotDto } from 'api/privacy/models/ScheduleSlotDto';
import styles from '../PrivacyTab.module.scss';

type Props = {
    entityId: string;
    entityType: 'driver' | 'vehicle';
    entityName: string;
    onClose: () => void;
    onSave: () => void;
};

type TimeInterval = { start: string; end: string };
type DayConfig = { allDay: boolean; times: TimeInterval[] };
type SpecificDateConfig = { date: string; allDay: boolean; times: TimeInterval[] };

const DAY_KEYS = [
    { dayOfWeek: 0, key: 'privacy.day_mon', fullKey: 'privacy.day_mon_full' },
    { dayOfWeek: 1, key: 'privacy.day_tue', fullKey: 'privacy.day_tue_full' },
    { dayOfWeek: 2, key: 'privacy.day_wed', fullKey: 'privacy.day_wed_full' },
    { dayOfWeek: 3, key: 'privacy.day_thu', fullKey: 'privacy.day_thu_full' },
    { dayOfWeek: 4, key: 'privacy.day_fri', fullKey: 'privacy.day_fri_full' },
    { dayOfWeek: 5, key: 'privacy.day_sat', fullKey: 'privacy.day_sat_full' },
    { dayOfWeek: 6, key: 'privacy.day_sun', fullKey: 'privacy.day_sun_full' },
];

const parseTimeStr = (str: string): { hour: number; minute: number } => {
    const [h, m] = (str || '00:00').split(':').map(Number);
    return { hour: h || 0, minute: m || 0 };
};

const formatTime = (hour: number, minute: number): string =>
    `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

const parseDateStr = (str: string): Date => {
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss" formats
    const dateOnly = str.split('T')[0];
    const [y, m, d] = dateOnly.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatDateStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// --- Inline time picker component ---
type TimeInputProps = {
    value: string;
    onChange: (value: string) => void;
};

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange }: TimeInputProps) => {
    const [showPicker, setShowPicker] = useState(false);
    const parsed = parseTimeStr(value);
    const triggerRef = useRef<HTMLDivElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerPos, setPickerPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    useEffect(() => {
        if (!showPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
                pickerRef.current && !pickerRef.current.contains(e.target as Node)
            ) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPicker]);

    const PICKER_HEIGHT = 360;

    const openPicker = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < PICKER_HEIGHT && rect.top > spaceBelow;
            setPickerPos({
                top: openAbove ? rect.top - PICKER_HEIGHT - 4 : rect.bottom + 4,
                left: rect.left,
            });
        }
        setShowPicker(true);
    };

    return (
        <>
            <div ref={triggerRef} style={{ width: '120px' }}>
                <InputGroup
                    value={formatTime(parsed.hour, parsed.minute)}
                    icon={<FaRegClock />}
                    readOnly
                    onClick={openPicker}
                    clickInText={true}
                    onClickInText={openPicker}
                    style={{ cursor: 'pointer' }}
                    slim
                />
            </div>
            {showPicker && ReactDOM.createPortal(
                <div
                    ref={pickerRef}
                    style={{ position: 'fixed', zIndex: 1100, top: pickerPos.top, left: pickerPos.left }}
                >
                    <Timekeeper
                        time={parsed}
                        hour24Mode={true}
                        closeOnMinuteSelect={true}
                        switchToMinuteOnHourSelect
                        doneButton={result => (
                            <TimeKeeperDoneButton onClick={() => {
                                setShowPicker(false);
                                if (result && result.formatted24) {
                                    const [h, m] = result.formatted24.split(':').map(Number);
                                    onChange(formatTime(h, m));
                                }
                            }} />
                        )}
                    />
                </div>,
                document.body,
            )}
        </>
    );
};

// --- Main component ---
const ScheduleModal: React.FC<Props> = ({ entityId, entityType, entityName, onClose, onSave }: Props) => {
    const { t } = useTranslation();
    const [weeklyOpen, setWeeklyOpen] = useState(true);
    const [datesOpen, setDatesOpen] = useState(true);
    const [activeDays, setActiveDays] = useState<number[]>([]);
    const [dayCfg, setDayCfg] = useState<Record<number, DayConfig>>({});
    const [specificDates, setSpecificDates] = useState<SpecificDateConfig[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [removeConfirm, setRemoveConfirm] = useState<{ action: () => void } | null>(null);

    useEffect(() => {
        const loadSchedule = async () => {
            try {
                setIsLoading(true);
                const data = await PrivacyService.getSchedule(entityId, entityType);
                if (data && data.length > 0) {
                    const recurringByDay: Record<number, DayConfig> = {};
                    const specificMap: Record<string, SpecificDateConfig> = {};

                    data.forEach(s => {
                        if (s.specificDate) {
                            const dateKey = s.specificDate.split('T')[0];
                            if (!specificMap[dateKey]) {
                                specificMap[dateKey] = { date: dateKey, allDay: false, times: [] };
                            }
                            if (s.allDay) {
                                specificMap[dateKey].allDay = true;
                                specificMap[dateKey].times = [];
                            } else {
                                specificMap[dateKey].times.push({ start: s.startTime || '09:00', end: s.endTime || '18:00' });
                            }
                        } else {
                            if (!recurringByDay[s.dayOfWeek]) {
                                recurringByDay[s.dayOfWeek] = { allDay: false, times: [] };
                            }
                            if (s.allDay) {
                                recurringByDay[s.dayOfWeek].allDay = true;
                                recurringByDay[s.dayOfWeek].times = [];
                            } else {
                                recurringByDay[s.dayOfWeek].times.push({ start: s.startTime || '09:00', end: s.endTime || '18:00' });
                            }
                        }
                    });

                    const activeKeys = Object.keys(recurringByDay).map(Number);
                    setActiveDays(activeKeys);
                    setDayCfg(recurringByDay);
                    setSpecificDates(Object.values(specificMap));
                }
            } catch {
                // Error handled by Http interceptor
            } finally {
                setIsLoading(false);
            }
        };
        loadSchedule();
    }, [entityId, entityType]);

    // --- Day chip toggle ---
    const handleDayChipToggle = (day: number) => {
        setActiveDays(prev => {
            if (prev.includes(day)) return prev.filter(d => d !== day);
            return [...prev, day];
        });
        if (!dayCfg[day]) {
            setDayCfg(prev => ({ ...prev, [day]: { allDay: true, times: [] } }));
        }
    };

    // --- Day config handlers ---
    const handleDayAllDayToggle = (day: number) => {
        setDayCfg(prev => {
            const current = prev[day] || { allDay: true, times: [] };
            if (current.allDay) {
                return { ...prev, [day]: { allDay: false, times: [{ start: '09:00', end: '18:00' }] } };
            }
            return { ...prev, [day]: { allDay: true, times: [] } };
        });
    };

    const handleAddDayInterval = (day: number) => {
        setDayCfg(prev => {
            const current = prev[day] || { allDay: false, times: [] };
            return { ...prev, [day]: { ...current, times: [...current.times, { start: '09:00', end: '18:00' }] } };
        });
    };

    const handleRemoveDayInterval = (day: number, idx: number) => {
        setDayCfg(prev => {
            const current = prev[day];
            if (!current) return prev;
            const newTimes = current.times.filter((_, i) => i !== idx);
            if (newTimes.length === 0) return { ...prev, [day]: { allDay: true, times: [] } };
            return { ...prev, [day]: { ...current, times: newTimes } };
        });
    };

    const handleDayTimeChange = (day: number, idx: number, field: 'start' | 'end', value: string) => {
        setDayCfg(prev => {
            const current = prev[day];
            if (!current) return prev;
            const newTimes = current.times.map((t, i) => i === idx ? { ...t, [field]: value } : t);
            return { ...prev, [day]: { ...current, times: newTimes } };
        });
    };

    // --- Specific dates handlers ---
    const handleAddSpecificDate = () => {
        setSpecificDates(prev => [...prev, {
            date: new Date().toISOString().split('T')[0],
            allDay: true,
            times: [],
        }]);
        if (!datesOpen) setDatesOpen(true);
    };

    const handleRemoveSpecificDate = (idx: number) => {
        setSpecificDates(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSpecificDateChange = (idx: number, value: string) => {
        setSpecificDates(prev => prev.map((sd, i) => i === idx ? { ...sd, date: value } : sd));
    };

    const handleSpecificDateAllDayToggle = (idx: number) => {
        setSpecificDates(prev => prev.map((sd, i) => {
            if (i !== idx) return sd;
            if (sd.allDay) return { ...sd, allDay: false, times: [{ start: '09:00', end: '18:00' }] };
            return { ...sd, allDay: true, times: [] };
        }));
    };

    const handleAddSpecificDateInterval = (idx: number) => {
        setSpecificDates(prev => prev.map((sd, i) => {
            if (i !== idx) return sd;
            return { ...sd, times: [...sd.times, { start: '09:00', end: '18:00' }] };
        }));
    };

    const handleRemoveSpecificDateInterval = (idx: number, timeIdx: number) => {
        setSpecificDates(prev => prev.map((sd, i) => {
            if (i !== idx) return sd;
            const newTimes = sd.times.filter((_, ti) => ti !== timeIdx);
            if (newTimes.length === 0) return { ...sd, allDay: true, times: [] };
            return { ...sd, times: newTimes };
        }));
    };

    const handleSpecificDateTimeChange = (idx: number, timeIdx: number, field: 'start' | 'end', value: string) => {
        setSpecificDates(prev => prev.map((sd, i) => {
            if (i !== idx) return sd;
            const newTimes = sd.times.map((t, ti) => ti === timeIdx ? { ...t, [field]: value } : t);
            return { ...sd, times: newTimes };
        }));
    };

    // --- Save ---
    const handleSave = async () => {
        try {
            setIsSaving(true);
            const allSlots: ScheduleSlotDto[] = [];

            if (activeDays.length > 0) {
                activeDays.forEach(day => {
                    const cfg = dayCfg[day] || { allDay: true, times: [] };
                    if (cfg.allDay) {
                        allSlots.push({ dayOfWeek: day, allDay: true, startTime: '00:00', endTime: '23:59', specificDate: null });
                    } else {
                        cfg.times.forEach(interval => {
                            allSlots.push({ dayOfWeek: day, allDay: false, startTime: interval.start, endTime: interval.end, specificDate: null });
                        });
                    }
                });
            }

            specificDates.forEach(sd => {
                if (sd.allDay) {
                    allSlots.push({ dayOfWeek: 0, allDay: true, startTime: '00:00', endTime: '23:59', specificDate: sd.date });
                } else {
                    sd.times.forEach(interval => {
                        allSlots.push({ dayOfWeek: 0, allDay: false, startTime: interval.start, endTime: interval.end, specificDate: sd.date });
                    });
                }
            });

            await PrivacyService.saveSchedule({
                entityId,
                entityType,
                slots: allSlots,
            });
            onSave();
        } catch {
            // Error handled by Http interceptor
        } finally {
            setIsSaving(false);
        }
    };

    const modalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '70vw',
            minWidth: '700px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            borderRadius: '16px',
            padding: '28px',
            border: '1px solid transparent',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            overflow: 'auto',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
        },
    };

    const confirmRemove = (action: () => void) => {
        setRemoveConfirm({ action });
    };

    const renderTimeIntervals = (
        times: TimeInterval[],
        onTimeChange: (idx: number, field: 'start' | 'end', value: string) => void,
        onRemove: (idx: number) => void,
    ) => (
        <div className={styles.scheduleIntervalsWrap}>
            {times.map((interval, idx) => (
                <div key={idx} className={styles.scheduleIntervalCard}>
                    <TimeInput value={interval.start} onChange={val => onTimeChange(idx, 'start', val)} />
                    <span className={styles.scheduleSep}>&mdash;</span>
                    <TimeInput value={interval.end} onChange={val => onTimeChange(idx, 'end', val)} />
                    <button type="button" className={styles.scheduleRemoveBtn} onClick={() => confirmRemove(() => onRemove(idx))}>
                        <FaTrashAlt />
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <Modal isOpen style={modalStyles} onRequestClose={onClose}>
            <div className={styles.scheduleModal}>
                <div className={styles.scheduleModalTitle}>
                    {t('privacy.schedule_title') as string}
                </div>
                <div className={styles.scheduleModalDesc}>
                    {entityName}
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>...</div>
                ) : (
                    <>
                        {/* WEEKLY SCHEDULE SECTION */}
                        <div className={styles.scheduleSection}>
                            <div className={styles.scheduleSectionHeader} onClick={() => setWeeklyOpen(prev => !prev)} style={{ cursor: 'pointer' }}>
                                <span className={styles.scheduleSectionTitle}>{t('privacy.weekly_schedule') as string}</span>
                                <span
                                    className={styles.chevronBtn}
                                    style={{ transform: weeklyOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                                >
                                    <FaChevronDown size={14} />
                                </span>
                            </div>

                            {weeklyOpen && (
                                <div className={styles.scheduleSectionBody}>
                                    <div className={styles.scheduleDayChips}>
                                        {DAY_KEYS.map(day => (
                                            <div
                                                key={day.dayOfWeek}
                                                className={`${styles.scheduleDayChip} ${activeDays.includes(day.dayOfWeek) ? styles.scheduleDayChipActive : ''}`}
                                                onClick={() => handleDayChipToggle(day.dayOfWeek)}
                                            >
                                                {t(day.fullKey as any) as string}
                                            </div>
                                        ))}
                                    </div>

                                    {[...activeDays].sort().map(day => {
                                        const cfg = dayCfg[day] || { allDay: true, times: [] };
                                        const dayKey = DAY_KEYS.find(d => d.dayOfWeek === day);
                                        return (
                                            <div key={day} className={styles.scheduleDayEntry}>
                                                <div className={styles.scheduleDayLabel}>
                                                    {dayKey ? (t(dayKey.fullKey as any) as string) : ''}
                                                </div>
                                                <div className={styles.scheduleDayBody}>
                                                    <div className={styles.scheduleAllDayRow}>
                                                        <CheckBox
                                                            label={t('privacy.all_day') as string}
                                                            checked={cfg.allDay}
                                                            onChange={() => handleDayAllDayToggle(day)}
                                                        />
                                                        {!cfg.allDay && (
                                                            <button type="button" className={styles.addSlotBtn} onClick={() => handleAddDayInterval(day)}>
                                                                + {t('privacy.add_interval') as string}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {!cfg.allDay && renderTimeIntervals(
                                                        cfg.times,
                                                        (idx, field, val) => handleDayTimeChange(day, idx, field, val),
                                                        idx => handleRemoveDayInterval(day, idx),
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* SPECIFIC DATES SECTION */}
                        <div className={styles.scheduleSection}>
                            <div className={styles.scheduleSectionHeader} style={{ cursor: 'pointer' }}>
                                <span className={styles.scheduleSectionTitle} onClick={() => setDatesOpen(prev => !prev)}>{t('privacy.specific_dates') as string}</span>
                                <div className={styles.scheduleSectionHeaderLeft}>
                                    <button type="button" className={styles.addSlotBtn} onClick={handleAddSpecificDate}>
                                        <FaPlus style={{ fontSize: '10px', marginRight: '4px' }} />
                                        {t('privacy.add_date') as string}
                                    </button>
                                    <span
                                        className={styles.chevronBtn}
                                        onClick={() => setDatesOpen(prev => !prev)}
                                        style={{ transform: datesOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                                    >
                                        <FaChevronDown size={14} />
                                    </span>
                                </div>
                            </div>

                            {datesOpen && (
                                <div className={styles.scheduleSectionBody}>
                                    {specificDates.map((sd, idx) => (
                                        <div key={idx} className={styles.scheduleDateBlock}>
                                            <div className={styles.scheduleDateBlockHeader}>
                                                <div className={styles.scheduleDateBlockHeaderLeft}>
                                                    <div className={styles.scheduleDatePickerWrap}>
                                                        <DateTimePicker
                                                            selected={parseDateStr(sd.date)}
                                                            onChange={date => {
                                                                if (date) handleSpecificDateChange(idx, formatDateStr(date as Date));
                                                            }}
                                                            dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                                            customInput={<InputGroup icon={<FaCalendarAlt />} clickInText={true} slim />}
                                                            popperClassName={styles.scheduleDatePopper}
                                                            popperProps={{ strategy: 'fixed', positionFixed: true } as any}
                                                        />
                                                    </div>
                                                    <CheckBox
                                                        label={t('privacy.all_day') as string}
                                                        checked={sd.allDay}
                                                        onChange={() => handleSpecificDateAllDayToggle(idx)}
                                                    />
                                                    {!sd.allDay && (
                                                        <button type="button" className={styles.addSlotBtn} onClick={() => handleAddSpecificDateInterval(idx)}>
                                                            + {t('privacy.add_interval') as string}
                                                        </button>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className={styles.scheduleRemoveBtn}
                                                    onClick={() => confirmRemove(() => handleRemoveSpecificDate(idx))}
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </div>
                                            {!sd.allDay && (
                                                <div className={styles.scheduleDayBody}>
                                                    {renderTimeIntervals(
                                                        sd.times,
                                                        (timeIdx, field, val) => handleSpecificDateTimeChange(idx, timeIdx, field, val),
                                                        timeIdx => handleRemoveSpecificDateInterval(idx, timeIdx),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {specificDates.length === 0 && (
                                        <div className={styles.scheduleNoData}>
                                            {t('privacy.no_specific_dates') as string}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className={styles.scheduleModalButtons}>
                    <Button
                        text={t('common.cancel') as string}
                        preset="secondary"
                        size="small"
                        onClick={onClose}
                    />
                    <Button
                        text={t('common.save') as string}
                        preset="primary"
                        size="small"
                        isLoading={isSaving}
                        onClick={handleSave}
                    />
                </div>
            </div>

            <QuestionYesNo
                isVisible={!!removeConfirm}
                message={t('common.messages.remove_record') as string}
                onYes={() => {
                    removeConfirm?.action();
                    setRemoveConfirm(null);
                }}
                onNo={() => setRemoveConfirm(null)}
            />
        </Modal>
    );
};

export default ScheduleModal;
